import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createHttpContextFromOptions } from '@quipex/core/primitives';
import { isResolvedProblemDetails } from '@quipex/core/utils';
import {
    BuildingsApiService,
    type BuildingDetailsResponse,
} from '@quipex/shared/contracts';
import { toAddressLocation, toBuildingNameFormat } from '@quipex/shared/utils';
import { timeout } from 'rxjs';
import {
    activityIdentitiesEqual,
    type ActivityIdentity,
} from '../activity-journal';
import type {
    ActivityBuildingDetails,
    ActivityBuildingDisplay,
} from '../activity-presentation/building-display';

export interface ActivityBuildingResolutionRequest {
    readonly identity: ActivityIdentity | null;
    readonly activeIdentityVersion: number;
    readonly buildingPublicIds: readonly string[];
}

export const ACTIVITY_BUILDING_LOOKUP_TIMEOUT_MS = 10_000;

@Injectable()
export class ActivityBuildingResolver {
    //#region Dependency injection
    private readonly buildingsApi = inject(BuildingsApiService);
    private readonly destroyRef = inject(DestroyRef);
    //#endregion

    //#region State
    private readonly cache = new Map<string, ActivityBuildingLookup>();
    private readonly inFlight = new Set<string>();
    private readonly settled = new Set<string>();
    private readonly lookupState = signal<
        ReadonlyMap<string, ActivityBuildingDisplay>
    >(new Map());
    private readonly deniedBuildingPublicIdState = signal<ReadonlySet<string>>(
        new Set()
    );
    private readonly settledBuildingPublicIdState = signal<ReadonlySet<string>>(
        new Set()
    );
    private readonly identityState = signal<ActivityIdentity | null>(null);
    private readonly activeIdentityVersionState = signal(0);
    private currentIds = new Set<string>();
    private generation = 0;

    public readonly lookups = this.lookupState.asReadonly();
    public readonly deniedBuildingPublicIds =
        this.deniedBuildingPublicIdState.asReadonly();
    public readonly settledBuildingPublicIds =
        this.settledBuildingPublicIdState.asReadonly();
    public readonly identity = this.identityState.asReadonly();
    public readonly activeIdentityVersion =
        this.activeIdentityVersionState.asReadonly();
    //#endregion

    //#region Public methods
    public resolve(request: ActivityBuildingResolutionRequest): void {
        const { identity, activeIdentityVersion, buildingPublicIds } = request;
        if (
            !activityIdentitiesEqual(this.identityState(), identity) ||
            this.activeIdentityVersionState() !== activeIdentityVersion
        ) {
            this.reset(identity, activeIdentityVersion);
        }

        this.currentIds = new Set(
            buildingPublicIds.filter((buildingPublicId) =>
                Boolean(buildingPublicId.trim())
            )
        );
        this.publishCurrentLookups();

        if (!identity) return;

        for (const buildingPublicId of this.currentIds) {
            if (
                this.cache.has(buildingPublicId) ||
                this.inFlight.has(buildingPublicId)
            ) {
                continue;
            }

            this.fetch(buildingPublicId);
        }
    }
    //#endregion

    //#region Private methods
    private fetch(buildingPublicId: string): void {
        const requestGeneration = this.generation;
        this.settled.delete(buildingPublicId);
        this.inFlight.add(buildingPublicId);

        this.buildingsApi
            .getBuildingById(buildingPublicId, {
                context: createHttpContextFromOptions({
                    // Activity enrichment is optional display context, so its failures must stay silent.
                    suppressAutoErrorToast: true,
                }),
            })
            .pipe(
                timeout({ first: ACTIVITY_BUILDING_LOOKUP_TIMEOUT_MS }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (building) => {
                    if (requestGeneration !== this.generation) return;

                    this.cache.set(buildingPublicId, {
                        status: 'resolved',
                        display: toBuildingDisplay(building),
                    });
                    this.inFlight.delete(buildingPublicId);
                    this.settled.add(buildingPublicId);
                    this.publishCurrentLookups();
                },
                error: (error: unknown) => {
                    if (requestGeneration !== this.generation) return;

                    this.inFlight.delete(buildingPublicId);
                    this.settled.add(buildingPublicId);
                    if (isDeniedBuildingLookup(error)) {
                        this.cache.set(buildingPublicId, { status: 'denied' });
                    }
                    this.publishCurrentLookups();
                },
                complete: () => {
                    if (requestGeneration !== this.generation) return;
                    this.inFlight.delete(buildingPublicId);
                    if (!this.settled.has(buildingPublicId)) {
                        this.settled.add(buildingPublicId);
                        this.publishCurrentLookups();
                    }
                },
            });
    }

    private publishCurrentLookups(): void {
        const currentLookups = new Map<string, ActivityBuildingDisplay>();
        const deniedBuildingPublicIds = new Set<string>();
        const settledBuildingPublicIds = new Set<string>();

        for (const buildingPublicId of this.currentIds) {
            const lookup = this.cache.get(buildingPublicId);
            if (this.settled.has(buildingPublicId)) {
                settledBuildingPublicIds.add(buildingPublicId);
            }
            if (lookup?.status === 'denied') {
                deniedBuildingPublicIds.add(buildingPublicId);
            } else if (lookup?.display) {
                currentLookups.set(buildingPublicId, lookup.display);
            }
        }

        this.lookupState.set(currentLookups);
        this.deniedBuildingPublicIdState.set(deniedBuildingPublicIds);
        this.settledBuildingPublicIdState.set(settledBuildingPublicIds);
    }

    private reset(
        identity: ActivityIdentity | null,
        activeIdentityVersion: number
    ): void {
        this.generation += 1;
        this.identityState.set(identity);
        this.activeIdentityVersionState.set(activeIdentityVersion);
        this.cache.clear();
        this.inFlight.clear();
        this.settled.clear();
        this.currentIds.clear();
        this.lookupState.set(new Map());
        this.deniedBuildingPublicIdState.set(new Set());
        this.settledBuildingPublicIdState.set(new Set());
    }
    //#endregion
}

type ActivityBuildingLookup =
    | {
          readonly status: 'resolved';
          readonly display: ActivityBuildingDisplay | null;
      }
    | { readonly status: 'denied' };

function isDeniedBuildingLookup(error: unknown): boolean {
    const status =
        error instanceof HttpErrorResponse || isResolvedProblemDetails(error)
            ? error.status
            : null;
    return status === 403 || status === 404 || status === 410;
}

function toBuildingDisplay(
    building: BuildingDetailsResponse
): ActivityBuildingDisplay | null {
    const rawDetails: ActivityBuildingDetails = {
        name: building.name,
        address: building.address,
    };
    const address = toAddressLocation(building.address);
    const label = withoutRepeatedLocation(
        toBuildingNameFormat(rawDetails).trim(),
        address
    );

    if (!label && !address) return null;

    return {
        details: {
            name: label || address,
            address: building.address,
        },
        location: address ? { buildingCity: address } : null,
        label: label || address,
        address: address || null,
    };
}

function withoutRepeatedLocation(label: string, location: string): string {
    if (!label || !location) return label;

    const suffix = `, ${location}`;
    return label.toLocaleLowerCase().endsWith(suffix.toLocaleLowerCase())
        ? label.slice(0, -suffix.length).trim()
        : label;
}
